import type { Crepe } from '@milkdown/crepe';
import type { useNodeViewFactory } from '@prosemirror-adapter/react';

import { videoPlugin } from './video';
import { configureVideoNodeView } from './node-view';
import type { MediaUploadHandler } from '../../types';
import { videoConfig, VideoSlashMenuItems, type SlashMenuItemConfig } from './slash-items';
import { videoInputRule } from './input-rules';

/** Minimal interface for slash menu group returned by builder.addGroup() */
interface SlashMenuGroup {
  addItem: (key: string, item: SlashMenuItemConfig) => SlashMenuGroup;
}

export interface ConfigureVideoOptions {
  /** Node view factory from useNodeViewFactory() */
  nodeViewFactory: ReturnType<typeof useNodeViewFactory>;
  /** Optional callback to upload video files. If not provided, uses blob URLs. */
  onUploadVideo?: MediaUploadHandler;
}

/**
 * Configure video blocks feature for the editor.
 * Installs video plugins, NodeView, and input rule.
 *
 * Usage:
 * ```ts
 * const nodeViewFactory = useNodeViewFactory();
 *
 * // In useEditor callback:
 * configureVideo(crepe, {
 *   nodeViewFactory,
 *   onUploadVideo: async (file) => {
 *     // Upload to S3 and return URL
 *     return uploadedUrl;
 *   }
 * });
 * ```
 */
export function configureVideo(crepe: Crepe, { nodeViewFactory, onUploadVideo }: ConfigureVideoOptions): void {
  // Register video plugins (remark transform, node schema)
  videoPlugin.forEach((plugin) => crepe.editor.use(plugin));

  // Register input rule for auto-converting pasted video URLs
  crepe.editor.use(videoInputRule);

  // Register NodeView for rendering videos
  crepe.editor.config((ctx) => configureVideoNodeView(ctx, nodeViewFactory));

  // Configure upload callback if provided
  if (onUploadVideo) {
    crepe.editor.config((ctx) => {
      ctx.set(videoConfig.key, { onUploadVideo });
    });
  }

  // Register the config context slice
  crepe.editor.use(videoConfig);
}

/**
 * Adds video item to a slash menu group.
 * Call this in Crepe's buildMenu callback.
 *
 * Usage:
 * ```ts
 * [Crepe.Feature.BlockEdit]: {
 *   buildMenu: (builder) => {
 *     const mediaGroup = builder.addGroup('media', 'Media');
 *     configureVideoSlashMenu(mediaGroup);
 *   },
 * },
 * ```
 */
export function configureVideoSlashMenu(group: SlashMenuGroup): void {
  group.addItem('video', VideoSlashMenuItems.video());
}

// Re-export slash menu items for manual configuration if needed
export { VideoSlashMenuItems };
